import { useEffect, useRef } from 'react';
import { subscribeToScroll, clamp01 } from './scrollEngine';

/* ==================================================================
   useScrollVar
   ------------------------------------------------------------------
   Publishes an element's scroll progress as a CSS custom property
   (default --hs-p, a unitless 0 -> 1). CSS then does the animating
   with calc()/clamp() on transforms and opacity, so scrolling never
   triggers a React render.

   modes
     cover - 0 as the element's top touches the viewport bottom,
             1 once its top reaches the viewport top. Good for
             "animate while the section arrives".
     exit  - 0 while the element's top is at the viewport top,
             1 once it has scrolled a full viewport past it. Good for
             "drift away as the next section takes over".
     enter - 0 -> 1 across the element's entire traversal of the
             viewport. Good for parallax.
     pin   - 0 -> 1 across a tall section that contains a sticky
             child (height - viewport). Drives pinned storytelling
             and horizontal scroll.
   ================================================================== */

export default function useScrollVar(options) {
  const {
    mode = 'cover',
    varName = '--hs-p',
    disabled = false,
    resetValue = 1,
  } = options || {};

  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (disabled) {
      node.style.setProperty(varName, String(resetValue));
      return undefined;
    }

    let last = -1;

    const measure = (viewport) => {
      const rect = node.getBoundingClientRect();
      if (mode === 'pin') {
        const travel = rect.height - viewport;
        return travel > 0 ? clamp01(-rect.top / travel) : rect.top <= 0 ? 1 : 0;
      }
      if (mode === 'exit') {
        return clamp01(-rect.top / Math.max(viewport, 1));
      }
      if (mode === 'enter') {
        return clamp01((viewport - rect.top) / (viewport + rect.height));
      }
      return clamp01((viewport - rect.top) / Math.max(viewport, 1));
    };

    const apply = (progress) => {
      /* Skip sub-perceptual changes; fewer style writes, same result. */
      if (Math.abs(progress - last) < 0.0008) return;
      last = progress;
      node.style.setProperty(varName, progress.toFixed(4));
    };

    return subscribeToScroll({ measure, apply });
  }, [mode, varName, disabled, resetValue]);

  return ref;
}
